import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { Switch as SwitchPrimitives } from "radix-ui";

import { cn } from "@/lib/cn";

const switchVariants = cva(
  "peer inline-flex shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent shadow-xs transition-colors disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:bg-primary data-[state=unchecked]:bg-input",
  {
    variants: {
      size: {
        md: "h-6 w-10",
        // 24px hit area for WCAG 2.5.8 while the visual box stays 20px
        sm: "relative h-5 w-9 after:absolute after:-inset-0.5",
      },
    },
    defaultVariants: {
      size: "md",
    },
  },
);

const thumbVariants = cva(
  "pointer-events-none block rounded-full bg-background ring-0 shadow-lg transition-transform",
  {
    variants: {
      size: {
        md: "size-5 data-[state=checked]:translate-x-4",
        sm: "size-4 data-[state=checked]:translate-x-4",
      },
    },
    defaultVariants: {
      size: "md",
    },
  },
);

export interface SwitchProps
  extends React.ComponentPropsWithoutRef<typeof SwitchPrimitives.Root>,
    VariantProps<typeof switchVariants> {}

const Switch = React.forwardRef<React.ElementRef<typeof SwitchPrimitives.Root>, SwitchProps>(
  ({ className, size, ...props }, ref) => (
    <SwitchPrimitives.Root className={cn(switchVariants({ size, className }))} {...props} ref={ref}>
      <SwitchPrimitives.Thumb className={cn(thumbVariants({ size }))} />
    </SwitchPrimitives.Root>
  ),
);
Switch.displayName = SwitchPrimitives.Root.displayName;

export { Switch };
